import { formStructureApi } from "api/TenantSlug/FormStructure/api.ts";
import { filterAtom } from "components/UI/BaseFilter/atoms/filterAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useRecoilState } from "recoil";
import { SearchFilterEnum } from "types/enums";

import { FilterRule, FilterType, FilterGroup } from "../types/filter";

import OfferData = App.Data.OfferData;
import FormStructureData = App.Data.FormStructureData;

export interface FilterColumn {
    dataIndex: string;
    title: string;
    filterType: FilterType;
    filterOptions?: Array<{ label: string; value: string | number | boolean }>;
    filterName?: string;
}

function defaultValueForType(type: string) {
    switch (type) {
        case "number":
            return 0;
        case "multiselect":
            return [];
        case "date":
        case "datetime":
            return null;
        case "boolean":
            return undefined;
        case "ratio":
            return { field1: "", field2: "", percentage: 0 };
        case "history_sent":
        case "history_rejected":
        case "history_accepted":
            return { timePeriod: "7_days" };
        case "history_rejected_reason":
            return { timePeriod: "7_days", reason: "" };
        case "external_api":
            return { url: "", method: "GET", expectedStatus: 200, timeout: 5000 };
        case "time_restriction":
            return {
                restrictionType: "weekday",
                excludedWeekdays: [],
                timeRanges: [{ start: "09:00", end: "17:00" }]
            };
        case "lead_count_limit":
            return { count: 10, period: "day" };
        default:
            return "";
    }
}

export const useBaseFilter = (offer?: OfferData) => {
    const [filterState, setFilters] = useRecoilState(filterAtom);
    const { t } = usePaldockTranslation("ui.offer-filter");

    // Fetch form structures for the offer
    const getAllFormStructures = useFetchAllPaginatedData((payload) => formStructureApi.index(payload), {
        filter: {
            filter: { id: { in: offer?.form_structures?.map((s) => s.id) ?? [] } },
            include: "formFields",
        },
    }, "formStructures", {
        enabled: Boolean(offer?.form_structures?.length)
    });
    const formStructures: FormStructureData[] = getAllFormStructures.data ?? [];

    // Helper function to map form field types to filter types
    const mapFormFieldTypeToFilterType = (formFieldType: string) => {
        switch (formFieldType) {
            case "text":
            case "email":
            case "phone":
            case "only-text":
            case "identity-card-number":
            case "nin":
            case "phone-cz":
            case "user-agent":
            case "bank-code":
            case "bank-account":
            case "cz-zip":
            case "house-number":
            case "text-title":
                return FILTER_TYPES.string;

            case "number":
            case "only-number":
            case "slider":
            case "slider-money":
            case "czk-price":
                return FILTER_TYPES.number;

            case "date":
            case "created-date":
                return FILTER_TYPES.date;

            case "checkbox":
            case "checkbox-terms":
                return FILTER_TYPES.boolean;

            case "select":
            case "radio":
            case "country":
                return FILTER_TYPES.select;

            default:
                return FILTER_TYPES.string;
        }
    };

    const getAvailableFilterColumns = (): FilterColumn[] => {
        const filters: FilterColumn[] = [
            // Affiliate Partner Filters
            {
                dataIndex: "lead_not_from_affiliate",
                title: t("filter.lead_not_from_affiliate.title"),
                filterType: FILTER_TYPES.boolean,
                filterName: t("filter.lead_not_from_affiliate.description"),
            },
            {
                dataIndex: "lead_from_affiliate",
                title: t("filter.lead_from_affiliate.title"),
                filterType: FILTER_TYPES.boolean,
                filterName: t("filter.lead_from_affiliate.description"),
            },
            {
                dataIndex: "leads_data",
                title: t("filter.leads_data.title"),
                filterType: FILTER_TYPES.boolean,
                filterName: t("filter.leads_data.description"),
            },

            // History Filters
            {
                dataIndex: "history_not_sent",
                title: t("filter.history_sent.title"),
                filterType: FILTER_TYPES.history_sent,
                filterName: t("filter.history_sent.description"),
                filterOptions: [
                    { label: t("filter.time_period.1_hour"), value: "1_hour" },
                    { label: t("filter.time_period.1_day"), value: "1_day" },
                    { label: t("filter.time_period.7_days"), value: "7_days" },
                    { label: t("filter.time_period.30_days"), value: "30_days" },
                    { label: t("filter.time_period.90_days"), value: "90_days" },
                    { label: t("filter.time_period.1_year"), value: "1_year" },
                ],
            },
            {
                dataIndex: "history_not_rejected",
                title: t("filter.history_rejected.title"),
                filterType: FILTER_TYPES.history_rejected,
                filterName: t("filter.history_rejected.description"),
                filterOptions: [
                    { label: t("filter.time_period.1_hour"), value: "1_hour" },
                    { label: t("filter.time_period.1_day"), value: "1_day" },
                    { label: t("filter.time_period.7_days"), value: "7_days" },
                    { label: t("filter.time_period.30_days"), value: "30_days" },
                    { label: t("filter.time_period.90_days"), value: "90_days" },
                    { label: t("filter.time_period.1_year"), value: "1_year" },
                ],
            },
            {
                dataIndex: "history_not_accepted",
                title: t("filter.history_accepted.title"),
                filterType: FILTER_TYPES.history_accepted,
                filterName: t("filter.history_accepted.description"),
                filterOptions: [
                    { label: t("filter.time_period.1_hour"), value: "1_hour" },
                    { label: t("filter.time_period.1_day"), value: "1_day" },
                    { label: t("filter.time_period.7_days"), value: "7_days" },
                    { label: t("filter.time_period.30_days"), value: "30_days" },
                    { label: t("filter.time_period.90_days"), value: "90_days" },
                    { label: t("filter.time_period.1_year"), value: "1_year" },
                ],
            },
            {
                dataIndex: "history_not_rejected_reason",
                title: t("filter.history_rejected_reason.title"),
                filterType: FILTER_TYPES.history_rejected_reason,
                filterName: t("filter.history_rejected_reason.description"),
                filterOptions: [
                    // Time period options
                    { label: t("filter.time_period.1_hour"), value: "1_hour" },
                    { label: t("filter.time_period.1_day"), value: "1_day" },
                    { label: t("filter.time_period.7_days"), value: "7_days" },
                    { label: t("filter.time_period.30_days"), value: "30_days" },
                    { label: t("filter.time_period.90_days"), value: "90_days" },
                    { label: t("filter.time_period.1_year"), value: "1_year" },
                ],
            },

            // External API Filter
            {
                dataIndex: "external_api_check",
                title: t("filter.external_api.title"),
                filterType: FILTER_TYPES.external_api,
                filterName: t("filter.external_api.description"),
                filterOptions: [
                    { label: t("filter.http_method.GET"), value: "GET" },
                    { label: t("filter.http_method.POST"), value: "POST" },
                    { label: t("filter.http_method.PUT"), value: "PUT" },
                    { label: t("filter.http_method.DELETE"), value: "DELETE" },
                ],
            },

            // Time Restriction Filter
            {
                dataIndex: "time_restriction_check",
                title: t("filter.time_restriction.title"),
                filterType: FILTER_TYPES.time_restriction,
                filterName: t("filter.time_restriction.description"),
                filterOptions: [
                    { label: t("filter.weekday.monday"), value: "1" },
                    { label: t("filter.weekday.tuesday"), value: "2" },
                    { label: t("filter.weekday.wednesday"), value: "3" },
                    { label: t("filter.weekday.thursday"), value: "4" },
                    { label: t("filter.weekday.friday"), value: "5" },
                    { label: t("filter.weekday.saturday"), value: "6" },
                    { label: t("filter.weekday.sunday"), value: "0" },
                ],
            },

            // Lead Count Limit Filter
            {
                dataIndex: "lead_count_limit_check",
                title: t("filter.lead_count_limit.title"),
                filterType: FILTER_TYPES.lead_count_limit,
                filterName: t("filter.lead_count_limit.description"),
                filterOptions: [
                    { label: t("filter.lead_count_limit.per_day"), value: "day" },
                    { label: t("filter.lead_count_limit.per_hour"), value: "hour" },
                ],
            },
        ];

        // Add form field filters if form structures are available
        if (formStructures.length > 0) {
            const fieldMap = new Map<string, {
                field: any,
                filterType: any,
                label: string
            }>();

            // Collect all fields and merge by system_name
            formStructures.forEach(formStructure => {
                if (formStructure.formFields) {
                    formStructure.formFields.forEach(formField => {
                        const systemName = formField.system_name;
                        if (!fieldMap.has(systemName)) {
                            const filterType = mapFormFieldTypeToFilterType(formField.type || "text");
                            const label = formField.system_name;

                            fieldMap.set(systemName, {
                                field: formField,
                                filterType,
                                label
                            });
                        }
                    });
                }
            });

            // Add filters for unique form fields
            fieldMap.forEach((fieldData, systemName) => {
                filters.push({
                    dataIndex: systemName,
                    title: fieldData.label,
                    filterType: fieldData.filterType,
                    filterName: t("filter.form_field.description", {
                        fieldName: fieldData.label
                    }),
                });
            });

            // Add ratio filter for percentage calculation between numeric fields
            const numericFields: Array<{field: string, label: string}> = [];
            fieldMap.forEach((fieldData, systemName) => {
                if (['number', 'only-number', 'slider', 'slider-money', 'czk-price'].includes(fieldData.field.type || '')) {
                    numericFields.push({
                        field: systemName,
                        label: fieldData.label
                    });
                }
            });

            if (numericFields.length >= 2) {
                filters.push({
                    dataIndex: "ratio_calculation",
                    title: t("filter.ratio.title"),
                    filterType: FILTER_TYPES.ratio,
                    filterName: t("filter.ratio.description"),
                    filterOptions: numericFields.map(field => ({
                        label: field.label,
                        value: field.field
                    })),
                });
            }
        }

        return filters;
    };

    const addFilter = (offer: OfferData, position?: number) => {
        const availableFilterColumns = getAvailableFilterColumns();
        if (!availableFilterColumns.length) return;
        const col = availableFilterColumns[0];

        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);

            let order = 0;
            if (existingOfferIndex >= 0) {
                const offerState = prev.offers[existingOfferIndex];
                const allItems = [
                    ...offerState.filters.map(f => ({ type: 'filter', order: f.order })),
                    ...offerState.filterGroups.map(g => ({ type: 'group', order: g.order }))
                ].sort((a, b) => a.order - b.order);

                if (position !== undefined && position <= allItems.length) {
                    // Insert at specific position
                    if (position === 0) {
                        order = allItems.length > 0 ? allItems[0].order - 1 : 0;
                    } else if (position >= allItems.length) {
                        order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                    } else {
                        const prevOrder = allItems[position - 1].order;
                        const nextOrder = allItems[position].order;
                        order = prevOrder + (nextOrder - prevOrder) / 2;
                    }
                } else {
                    // Add to end
                    order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                }
            }

            const newFilter = {
                id: Date.now(),
                dataIndex: col.dataIndex,
                filterName: col.filterName ?? col.dataIndex,
                filterType: col.filterType,
                filterOptions: col.filterOptions || [],
                operator: col.filterType.operators[0] as SearchFilterEnum,
                value: defaultValueForType(col.filterType.type),
                logicalOperator: "and" as const,
                order,
            };

            if (existingOfferIndex >= 0) {
                const updatedOffers = [...prev.offers];
                updatedOffers[existingOfferIndex] = {
                    ...updatedOffers[existingOfferIndex],
                    filters: [...updatedOffers[existingOfferIndex].filters, newFilter]
                };
                return { ...prev, offers: updatedOffers };
            } else {
                return {
                    ...prev,
                    offers: [...prev.offers, { offerId: offer.id, filters: [newFilter], filterGroups: [] }]
                };
            }
        });
    };

    const updateFilter = (offer: OfferData, id: number, key: keyof FilterRule, value: unknown) => {
        const availableFilterColumns = getAvailableFilterColumns();

        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedFilters = updatedOffers[existingOfferIndex].filters.map((f) => {
                if (f.id !== id) return f;

                if (key === "dataIndex") {
                    const newColumn = availableFilterColumns.find((col: FilterColumn) => col.dataIndex === value);
                    if (!newColumn) return f;

                    const op = newColumn.filterType.operators[0] as SearchFilterEnum;
                    const val = defaultValueForType(newColumn.filterType.type);

                    return {
                        ...f,
                        dataIndex: newColumn.dataIndex,
                        filterName: newColumn.filterName,
                        filterType: newColumn.filterType,
                        filterOptions: newColumn.filterOptions || [],
                        operator: op,
                        value: val,
                        logicalOperator: f.logicalOperator || "and",
                    };
                }

                return { ...f, [key]: value };
            });

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filters: updatedFilters
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const removeFilter = (offer: OfferData, id: number) => {
        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedFilters = updatedOffers[existingOfferIndex].filters.filter((f) => f.id !== id);

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filters: updatedFilters
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const clearFilters = (offer: OfferData) => {
        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filters: [],
                filterGroups: []
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const getOfferFilters = (offer: OfferData) => {
        const offerFilterState = filterState.offers.find((offerState) => offerState.offerId === offer.id);
        return offerFilterState?.filters ?? [];
    };

    const getOfferFilterGroups = (offer: OfferData) => {
        const offerFilterState = filterState.offers.find((offerState) => offerState.offerId === offer.id);
        return offerFilterState?.filterGroups ?? [];
    };

    // CASCADE FILTER FUNCTIONS
    const getCascadeFilters = (cascadeId: string) => {
        const cascadeFilterState = filterState.cascades.find((cascadeState) => cascadeState.cascadeId === cascadeId);
        return cascadeFilterState?.filters ?? [];
    };

    const getCascadeFilterGroups = (cascadeId: string) => {
        const cascadeFilterState = filterState.cascades.find((cascadeState) => cascadeState.cascadeId === cascadeId);
        return cascadeFilterState?.filterGroups ?? [];
    };

    const addCascadeFilter = (cascadeId: string, position?: number) => {
        const availableFilterColumns = getAvailableFilterColumns();
        if (!availableFilterColumns.length) return;
        const col = availableFilterColumns[0];

        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);

            let order = 0;
            if (existingCascadeIndex >= 0) {
                const cascadeState = prev.cascades[existingCascadeIndex];
                const allItems = [
                    ...cascadeState.filters.map(f => ({ type: 'filter', order: f.order })),
                    ...cascadeState.filterGroups.map(g => ({ type: 'group', order: g.order }))
                ].sort((a, b) => a.order - b.order);

                if (position !== undefined && position <= allItems.length) {
                    // Insert at specific position
                    if (position === 0) {
                        order = allItems.length > 0 ? allItems[0].order - 1 : 0;
                    } else if (position >= allItems.length) {
                        order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                    } else {
                        const prevOrder = allItems[position - 1].order;
                        const nextOrder = allItems[position].order;
                        order = prevOrder + (nextOrder - prevOrder) / 2;
                    }
                } else {
                    // Add to end
                    order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                }
            }

            const newFilter = {
                id: Date.now(),
                dataIndex: col.dataIndex,
                filterName: col.filterName ?? col.dataIndex,
                filterType: col.filterType,
                filterOptions: col.filterOptions || [],
                operator: col.filterType.operators[0] as SearchFilterEnum,
                value: defaultValueForType(col.filterType.type),
                logicalOperator: "and" as const,
                order,
            };

            if (existingCascadeIndex >= 0) {
                const updatedCascades = [...prev.cascades];
                updatedCascades[existingCascadeIndex] = {
                    ...updatedCascades[existingCascadeIndex],
                    filters: [...updatedCascades[existingCascadeIndex].filters, newFilter]
                };
                return { ...prev, cascades: updatedCascades };
            } else {
                return {
                    ...prev,
                    cascades: [...prev.cascades, { cascadeId, filters: [newFilter], filterGroups: [] }]
                };
            }
        });
    };

    const updateCascadeFilter = (cascadeId: string, id: number, key: keyof FilterRule, value: unknown) => {
        const availableFilterColumns = getAvailableFilterColumns();

        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedFilters = updatedCascades[existingCascadeIndex].filters.map((f) => {
                if (f.id !== id) return f;

                if (key === "dataIndex") {
                    const newColumn = availableFilterColumns.find((col: FilterColumn) => col.dataIndex === value);
                    if (!newColumn) return f;

                    const op = newColumn.filterType.operators[0] as SearchFilterEnum;
                    const val = defaultValueForType(newColumn.filterType.type);

                    return {
                        ...f,
                        dataIndex: newColumn.dataIndex,
                        filterName: newColumn.filterName,
                        filterType: newColumn.filterType,
                        filterOptions: newColumn.filterOptions || [],
                        operator: op,
                        value: val,
                        logicalOperator: f.logicalOperator || "and",
                    };
                }

                return { ...f, [key]: value };
            });

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filters: updatedFilters
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const removeCascadeFilter = (cascadeId: string, id: number) => {
        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedFilters = updatedCascades[existingCascadeIndex].filters.filter((f) => f.id !== id);

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filters: updatedFilters
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const clearCascadeFilters = (cascadeId: string) => {
        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filters: [],
                filterGroups: []
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const addFilterGroup = (offer: OfferData, position?: number) => {
        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);

            let order = 0;
            if (existingOfferIndex >= 0) {
                const offerState = prev.offers[existingOfferIndex];
                const allItems = [
                    ...offerState.filters.map(f => ({ type: 'filter', order: f.order })),
                    ...offerState.filterGroups.map(g => ({ type: 'group', order: g.order }))
                ].sort((a, b) => a.order - b.order);

                if (position !== undefined && position <= allItems.length) {
                    // Insert at specific position
                    if (position === 0) {
                        order = allItems.length > 0 ? allItems[0].order - 1 : 0;
                    } else if (position >= allItems.length) {
                        order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                    } else {
                        const prevOrder = allItems[position - 1].order;
                        const nextOrder = allItems[position].order;
                        order = prevOrder + (nextOrder - prevOrder) / 2;
                    }
                } else {
                    // Add to end
                    order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                }
            }

            const newGroup: FilterGroup = {
                id: Date.now(),
                name: t("filter.group.name"),
                filters: [],
                logicalOperator: "and",
                internalLogicalOperator: "and",
                order
            };

            if (existingOfferIndex >= 0) {
                const updatedOffers = [...prev.offers];
                updatedOffers[existingOfferIndex] = {
                    ...updatedOffers[existingOfferIndex],
                    filterGroups: [...updatedOffers[existingOfferIndex].filterGroups, newGroup]
                };
                return { ...prev, offers: updatedOffers };
            } else {
                return {
                    ...prev,
                    offers: [...prev.offers, { offerId: offer.id, filters: [], filterGroups: [newGroup] }]
                };
            }
        });
    };

    const removeFilterGroup = (offer: OfferData, groupId: number) => {
        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedGroups = updatedOffers[existingOfferIndex].filterGroups.filter((g) => g.id !== groupId);

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const addFilterToGroup = (offer: OfferData, groupId: number) => {
        const availableFilterColumns = getAvailableFilterColumns();
        if (!availableFilterColumns.length) return;
        const col = availableFilterColumns[0];

        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedGroups = updatedOffers[existingOfferIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    let order = 0;
                    if (group.filters.length > 0) {
                        const sortedFilters = [...group.filters].sort((a, b) => a.order - b.order);
                        order = sortedFilters[sortedFilters.length - 1].order + 1;
                    }

                    const newFilter = {
                        id: Date.now(),
                        dataIndex: col.dataIndex,
                        filterName: col.filterName ?? col.dataIndex,
                        filterType: col.filterType,
                        filterOptions: col.filterOptions || [],
                        operator: col.filterType.operators[0] as SearchFilterEnum,
                        value: defaultValueForType(col.filterType.type),
                        logicalOperator: "and" as const,
                        order,
                    };

                    return {
                        ...group,
                        filters: [...group.filters, newFilter]
                    };
                }
                return group;
            });

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const updateFilterInGroup = (offer: OfferData, groupId: number, filterId: number, key: keyof FilterRule, value: unknown) => {
        const availableFilterColumns = getAvailableFilterColumns();

        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedGroups = updatedOffers[existingOfferIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    const updatedFilters = group.filters.map((f) => {
                        if (f.id !== filterId) return f;

                        if (key === "dataIndex") {
                            const newColumn = availableFilterColumns.find((col: FilterColumn) => col.dataIndex === value);
                            if (!newColumn) return f;

                            const op = newColumn.filterType.operators[0] as SearchFilterEnum;
                            const val = defaultValueForType(newColumn.filterType.type);

                            return {
                                ...f,
                                dataIndex: newColumn.dataIndex,
                                filterName: newColumn.filterName,
                                filterType: newColumn.filterType,
                                filterOptions: newColumn.filterOptions || [],
                                operator: op,
                                value: val,
                                logicalOperator: f.logicalOperator || "and",
                            };
                        }

                        return { ...f, [key]: value };
                    });

                    return {
                        ...group,
                        filters: updatedFilters
                    };
                }
                return group;
            });

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const removeFilterFromGroup = (offer: OfferData, groupId: number, filterId: number) => {
        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedGroups = updatedOffers[existingOfferIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    return {
                        ...group,
                        filters: group.filters.filter((f) => f.id !== filterId)
                    };
                }
                return group;
            });

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const updateFilterGroup = (offer: OfferData, groupId: number, key: keyof FilterGroup, value: unknown) => {
        setFilters((prev) => {
            const existingOfferIndex = prev.offers.findIndex(o => o.offerId === offer.id);
            if (existingOfferIndex === -1) return prev;

            const updatedOffers = [...prev.offers];
            const updatedGroups = updatedOffers[existingOfferIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    return { ...group, [key]: value };
                }
                return group;
            });

            updatedOffers[existingOfferIndex] = {
                ...updatedOffers[existingOfferIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, offers: updatedOffers };
        });
    };

    const addCascadeFilterGroup = (cascadeId: string, position?: number) => {
        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);

            let order = 0;
            if (existingCascadeIndex >= 0) {
                const cascadeState = prev.cascades[existingCascadeIndex];
                const allItems = [
                    ...cascadeState.filters.map(f => ({ type: 'filter', order: f.order })),
                    ...cascadeState.filterGroups.map(g => ({ type: 'group', order: g.order }))
                ].sort((a, b) => a.order - b.order);

                if (position !== undefined && position <= allItems.length) {
                    // Insert at specific position
                    if (position === 0) {
                        order = allItems.length > 0 ? allItems[0].order - 1 : 0;
                    } else if (position >= allItems.length) {
                        order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                    } else {
                        const prevOrder = allItems[position - 1].order;
                        const nextOrder = allItems[position].order;
                        order = prevOrder + (nextOrder - prevOrder) / 2;
                    }
                } else {
                    // Add to end
                    order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0;
                }
            }

            const newGroup: FilterGroup = {
                id: Date.now(),
                name: t("filter.group.name"),
                filters: [],
                logicalOperator: "and",
                internalLogicalOperator: "and",
                order
            };

            if (existingCascadeIndex >= 0) {
                const updatedCascades = [...prev.cascades];
                updatedCascades[existingCascadeIndex] = {
                    ...updatedCascades[existingCascadeIndex],
                    filterGroups: [...updatedCascades[existingCascadeIndex].filterGroups, newGroup]
                };
                return { ...prev, cascades: updatedCascades };
            } else {
                return {
                    ...prev,
                    cascades: [...prev.cascades, { cascadeId, filters: [], filterGroups: [newGroup] }]
                };
            }
        });
    };

    const removeCascadeFilterGroup = (cascadeId: string, groupId: number) => {
        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedGroups = updatedCascades[existingCascadeIndex].filterGroups.filter((g) => g.id !== groupId);

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const addCascadeFilterToGroup = (cascadeId: string, groupId: number) => {
        const availableFilterColumns = getAvailableFilterColumns();
        if (!availableFilterColumns.length) return;
        const col = availableFilterColumns[0];

        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedGroups = updatedCascades[existingCascadeIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    let order = 0;
                    if (group.filters.length > 0) {
                        const sortedFilters = [...group.filters].sort((a, b) => a.order - b.order);
                        order = sortedFilters[sortedFilters.length - 1].order + 1;
                    }

                    const newFilter = {
                        id: Date.now(),
                        dataIndex: col.dataIndex,
                        filterName: col.filterName ?? col.dataIndex,
                        filterType: col.filterType,
                        filterOptions: col.filterOptions || [],
                        operator: col.filterType.operators[0] as SearchFilterEnum,
                        value: defaultValueForType(col.filterType.type),
                        logicalOperator: "and" as const,
                        order,
                    };

                    return {
                        ...group,
                        filters: [...group.filters, newFilter]
                    };
                }
                return group;
            });

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const updateCascadeFilterInGroup = (cascadeId: string, groupId: number, filterId: number, key: keyof FilterRule, value: unknown) => {
        const availableFilterColumns = getAvailableFilterColumns();

        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedGroups = updatedCascades[existingCascadeIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    const updatedFilters = group.filters.map((f) => {
                        if (f.id !== filterId) return f;

                        if (key === "dataIndex") {
                            const newColumn = availableFilterColumns.find((col: FilterColumn) => col.dataIndex === value);
                            if (!newColumn) return f;

                            const op = newColumn.filterType.operators[0] as SearchFilterEnum;
                            const val = defaultValueForType(newColumn.filterType.type);

                            return {
                                ...f,
                                dataIndex: newColumn.dataIndex,
                                filterName: newColumn.filterName,
                                filterType: newColumn.filterType,
                                filterOptions: newColumn.filterOptions || [],
                                operator: op,
                                value: val,
                                logicalOperator: f.logicalOperator || "and",
                            };
                        }

                        return { ...f, [key]: value };
                    });

                    return {
                        ...group,
                        filters: updatedFilters
                    };
                }
                return group;
            });

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const removeCascadeFilterFromGroup = (cascadeId: string, groupId: number, filterId: number) => {
        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedGroups = updatedCascades[existingCascadeIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    return {
                        ...group,
                        filters: group.filters.filter((f) => f.id !== filterId)
                    };
                }
                return group;
            });

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    const updateCascadeFilterGroup = (cascadeId: string, groupId: number, key: keyof FilterGroup, value: unknown) => {
        setFilters((prev) => {
            const existingCascadeIndex = prev.cascades.findIndex(c => c.cascadeId === cascadeId);
            if (existingCascadeIndex === -1) return prev;

            const updatedCascades = [...prev.cascades];
            const updatedGroups = updatedCascades[existingCascadeIndex].filterGroups.map((group) => {
                if (group.id === groupId) {
                    return { ...group, [key]: value };
                }
                return group;
            });

            updatedCascades[existingCascadeIndex] = {
                ...updatedCascades[existingCascadeIndex],
                filterGroups: updatedGroups
            };

            return { ...prev, cascades: updatedCascades };
        });
    };

    return {
        getAvailableFilterColumns,
        addFilter,
        updateFilter,
        removeFilter,
        clearFilters,
        getOfferFilters,
        getOfferFilterGroups,
        addFilterGroup,
        removeFilterGroup,
        addFilterToGroup,
        updateFilterInGroup,
        removeFilterFromGroup,
        updateFilterGroup,
        // Cascade functions
        getCascadeFilters,
        getCascadeFilterGroups,
        addCascadeFilter,
        updateCascadeFilter,
        removeCascadeFilter,
        clearCascadeFilters,
        addCascadeFilterGroup,
        removeCascadeFilterGroup,
        addCascadeFilterToGroup,
        updateCascadeFilterInGroup,
        removeCascadeFilterFromGroup,
        updateCascadeFilterGroup,
    };
};
